// themeSlice.ts
import { createSlice } from '@reduxjs/toolkit';

export const CURRENT_VIEW = {
    CHAT_VIEW: 'CHAT_VIEW',
    MESSAGE_VIEW: 'MESSAGE_VIEW',
    NEW_CHAT_VIEW: 'NEW_CHAT_VIEW',
};

const MESSAGE_ADD_TYPE = {
    APPEND: 'append',
    PREPEND: 'prepend',
};

const initialState = {
    chatList: [],
    messages: [],
    selectedUser: {},
    chatNotifications: 0,
    isChatOpen: false,
    currentView: CURRENT_VIEW.CHAT_VIEW,
    onlineUsers: [],
    messageAddType: MESSAGE_ADD_TYPE.APPEND,
    chatUsers: []
};

const chatSlice = createSlice({
    name: `chat_slice`,
    initialState,
    reducers: {
        initializeChatList: (state, action) => {
            state.chatList = action.payload.sort((a, b) => new Date(b.date) - new Date(a.date));
            const totalNotifications = action?.payload.reduce((acc, cv) => (acc += cv.unread), 0);
            state.chatNotifications = totalNotifications;
            state.messageAddType = MESSAGE_ADD_TYPE.APPEND;
        },
        initializeMessages: (state, action) => {
            state.messages = action.payload;
        },
        initializeChatUsers: (state, action) => {
            state.chatUsers = action.payload;
        },
        prependMessages: (state, action) => {
            if (!action.payload || !Array.isArray(action.payload)) {
                throw new Error('Invalid message data provided');
            }
            state.messages = [...action.payload, ...state.messages];
            state.messageAddType = MESSAGE_ADD_TYPE.PREPEND;
        },
        setSelectedUser: (state, action) => {
            if (!action.payload || typeof action.payload !== 'object') {
                state.selectedUser = null;
                throw new Error('Invalid user data provided');
            }
            state.selectedUser = action.payload;
        },
        addMessage: (state, action) => {
            if (!action.payload || typeof action.payload !== 'object') {
                throw new Error('Invalid message data provided');
            }
            const newMessages = [...state.messages, action.payload];
            state.messages = newMessages;
            state.messageAddType = MESSAGE_ADD_TYPE.APPEND;
            const chatId = action.payload.chat_id;
            const updatedChatList = state.chatList.map((chat) => {
                if (chat.chat_id === chatId) {
                    return {
                        ...chat,
                        unread: chat.unread + 1,
                    };
                }
                return chat;
            });
            state.chatList = updatedChatList;
        },
        deleteMessage: (state, action) => {
            if (!action.payload || typeof action.payload !== 'string') {
                throw new Error('Invalid message data provided');
            }
            const newMessages = state.messages.filter((msg) => msg.id !== action.payload);
            state.messages = newMessages;
        },
        addChatList: (state, action) => {
            if (!action.payload || typeof action.payload !== 'object') {
                throw new Error('Invalid message data provided');
            }
            const newChatList = [action.payload, ...state.chatList];
            state.chatList = newChatList;
        },
        removeChatList: (state, action) => {
            if (!action.payload || typeof action.payload !== 'string') {
                throw new Error('Invalid message data provided');
            }
            const newChatList = state.chatList.filter((chat) => chat.id !== action.payload);
            state.chatList = newChatList;
        },
        incrementChatNotification: (state) => {
            state.chatNotifications = state.chatNotifications + 1;
        },
        resetChatNotification: (state) => {
            state.chatNotifications = null;
        },
        toggleChatOpen: (state) => {
            state.isChatOpen = !state.isChatOpen;
        },
        closeChat: (state) => {
            state.isChatOpen = false;
        },
        openChat: (state) => {
            state.isChatOpen = true;
        },
        setCurrentView: (state, action) => {
            if (!Object.values(CURRENT_VIEW).includes(action.payload)) {
                throw new Error('current view must be CHAT_VIEW or MESSAGE_VIEW');
            }
            state.currentView = action.payload;
        },
        setOnlineUsers: (state, action) => {
            if (!action.payload || !Array.isArray(action.payload)) {
                throw new Error('Invalid message data provided');
            }
            state.onlineUsers = action.payload;
        },
        addOnlineUser: (state, action) => {
            const onlineUsers = [...state.onlineUsers, action.payload];
            state.onlineUsers = onlineUsers;
        },
        removeOnlineUser: (state, action) => {
            const onlineUsers = state.onlineUsers.filter((id) => id !== action.payload);
            state.onlineUsers = onlineUsers;
        },
        setMessageAddType: (state, action) => {
            if (!Object.values(MESSAGE_ADD_TYPE).includes(action.payload)) {
                throw new Error('message add type must be APPEND or PREPEND');
            }
            state.messageAddType = action.payload;
        },
    },
});

export const {
    initializeChatList,
    initializeMessages,
    prependMessages,
    setSelectedUser,
    addMessage,
    deleteMessage,
    addChatList,
    removeChatList,
    incrementChatNotification,
    resetChatNotification,
    toggleChatOpen,
    closeChat,
    openChat,
    setCurrentView,
    setOnlineUsers,
    addOnlineUser,
    removeOnlineUser,
    setMessageAddType,
    initializeChatUsers,
} = chatSlice.actions;
export default chatSlice.reducer;
